/**
 * Megaproject form grammar: shared types (D-1120/D-1121, card B). View-side and pure: no sim, no three.js.
 * Coordinates are metres, y up, origin at the centre of the body's foot. Rotations are Euler XYZ as in three.js
 * (matrix Rx·Ry·Rz, so a part is tilted about z first, then yawed about y).
 */

export const MEGA_KINDS = [
  'longHold',
  'listeningSails',
  'trunkLine',
  'oreSling',
  'provingRange',
  'span',
  'commons',
  'colossusSlip',
  'crownFork',
] as const
export type MegaKind = (typeof MEGA_KINDS)[number]

/** Makers are faction data ids (`src/data/factions.json`). */
export const MEGA_MAKERS = ['holdfast', 'mutual', 'authority', 'meridian', 'tethys', 'varro_security'] as const
export type MegaMaker = (typeof MEGA_MAKERS)[number]

export type MegaRegion = 'gate' | 'mid' | 'deep' | 'rim'

export interface SiteContext {
  readonly region: MegaRegion
  /** Star luminosity in solar units; a dim star gets bigger sails and collectors. */
  readonly starLuminosity: number
  /** Bearing of the system's gate from the anchor, radians in the disc plane (0 = +x, π/2 = +z). */
  readonly gateBearingRad: number
  /** The system's ore signature colour (`#rrggbb`); tints the Crown Fork rock. */
  readonly oreTint?: string
}

export type PrimKind =
  | 'drum' // cylinder on local y: diameter size.x/size.z, height size.y
  | 'frustum' // cone frustum on local y: bottom diameter size.x/size.z, top = bottom × taper
  | 'slab' // box; top face x-extent × taper, top face shifted +x by shear × height
  | 'sphere' // ellipsoid with extents size
  | 'plate' // thin box (a panel); same shape rules as slab
  | 'dish' // spherical cap opening to local +y: diameter size.x/size.z, depth size.y
  | 'arc' // torus segment in local x-y: outer diameter size.x, tube diameter size.z, `arc` radians centred on +y
  | 'relicArc' // a First Rush wheel segment (same shape as arc)
  | 'blade' // raked slab (taper + shear)
  | 'podTier' // rounded pod tier on local y (Meridian): diameter size.x/size.z, height size.y
  | 'coilGate' // chamfered-square gate frame, opening along local y: outer size.x/size.z, depth size.y
  | 'tine' // tapered, raked claw (slab rules)
  | 'rock' // lumpy ellipsoid (seeded by `seed`)

export type PartRole = 'body' | 'signature' | 'accent' | 'relic' | 'work'

export type Vec3 = readonly [number, number, number]

export interface MegaPart {
  readonly prim: PrimKind
  readonly pos: Vec3
  readonly rot: Vec3
  readonly size: Vec3
  /** Top/bottom scale for frustum, slab, plate, blade and tine; 1 otherwise. */
  readonly taper: number
  /** Top face x-offset per metre of height for slab, plate, blade and tine; 0 otherwise. */
  readonly shear: number
  /** Arc angle in radians for arc and relicArc (2π = whole wheel); 0 otherwise. */
  readonly arc: number
  /** Build step 1..5 at which the part appears. */
  readonly step: number
  readonly role: PartRole
  /** Shape seed for rocks; 0 otherwise. */
  readonly seed: number
  /** Ore tint for rock parts. */
  readonly tint?: string
}

export type FamilyId = `${MegaKind}/${MegaMaker}`

export interface FormGenome {
  readonly family: FamilyId
  /** Silhouette-level slots, each an option index (≥ 6 slots, 3–4 options each). */
  readonly slots: readonly number[]
  /** Continuous proportions in [0, 1). */
  readonly props: readonly number[]
  /** Detail seed (uint32) for sub-silhouette scatter: pod sizes, rock lumps. */
  readonly detail: number
  /** Printable slot code, e.g. `2031-1302`. */
  readonly code: string
}

export interface SlotDef {
  readonly name: string
  readonly options: number
  /** Set for site-coupled slots: the option is read off the site, not drawn from the seed. */
  readonly site?: (site: SiteContext) => number
}

export interface FamilyGrammar {
  readonly kind: MegaKind
  readonly maker: MegaMaker
  readonly slots: readonly SlotDef[]
  /** The whole build; parts carry their step. `stage` lets a grammar fold or unfold parts in place. */
  readonly build: (g: FormGenome, site: SiteContext, stage: number) => MegaPart[]
}

export const MEGA_STAGES = 5
export const PROP_COUNT = 6
